/**
 * PB-FND-42 — Unified flush → encode facade.
 * One pipeline: flushBuilderState(manifest) → encodeTier1Properties(manifest).
 */

import { collectAiImageDraftFromDom, collectAiTextDraftFromDom } from '@theme/pb-dl-ai-hydrate';
import { applyDerivedPropertiesToSelections } from '@theme/pb-derived-properties-logic';

/** Customer-safe message when Tier-1 encode belt modules are not loaded. */
export const ENCODE_BELT_NOT_READY_MESSAGE =
  'Personalization could not be saved. Please refresh the page and try again.';

/**
 * Fail loud when encode belt globals are missing — never degrade to selections-only props.
 *
 * @param {object} [pb]
 * @param {string} [context] diagnostic tag for tests/logging
 */
/** True when tier-1 cart encode is installed on productBuilder (not finalize-only stubs). */
export function areEncodeBeltReady(pb) {
  const productBuilder = pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  return !!productBuilder && typeof productBuilder.encodeBuilderStateForCart === 'function';
}

export function assertEncodeBeltReady(pb, context) {
  const productBuilder = pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  if (!areEncodeBeltReady(productBuilder)) {
    const err = new Error(ENCODE_BELT_NOT_READY_MESSAGE);
    err.code = 'PB_ENCODE_BELT_NOT_READY';
    if (context) err.context = context;
    throw err;
  }
  return productBuilder;
}

/**
 * @param {object} [manifest]
 * @returns {string}
 */
export function resolveFlowContainerSelector(manifest) {
  const m = manifest || {};
  if (m.containerSelector) return m.containerSelector;
  if (typeof window !== 'undefined' && window.builderState?.mode === 'SUB') {
    return '#sub-flow-container';
  }
  return '#main-flow-container';
}

/**
 * Flush DOM field adapters into builder state before encode.
 * Registry flush only — no PBDesignLibrary.flushCartPropsToSelections side door.
 *
 * @param {object} [manifest]
 * @param {{ pb?: object }} [deps]
 */
export function flushBuilderState(manifest, deps) {
  const pb = deps?.pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  const containerSelector = resolveFlowContainerSelector(manifest);

  if (typeof window !== 'undefined' && window.PBAiImage?.flushAllFromDom) {
    window.PBAiImage.flushAllFromDom(containerSelector);
    if (typeof document !== 'undefined') {
      window.PBAiImage.flushAllFromDom(document);
    }
  }
  if (typeof window !== 'undefined' && window.PBAiText?.flushAllFromDom) {
    window.PBAiText.flushAllFromDom(containerSelector);
    if (typeof document !== 'undefined') {
      window.PBAiText.flushAllFromDom(document);
    }
  }
  if (typeof pb?.flushTopicAnswersFromDom === 'function') {
    pb.flushTopicAnswersFromDom(containerSelector);
  }
  if (typeof pb?.flushRegistryFlowFields === 'function') {
    pb.flushRegistryFlowFields(containerSelector);
  }
  flushDerivedPropertiesForManifest(manifest);
}

function resolveFieldConfigForFlush(manifest) {
  const m = manifest || {};
  if (Array.isArray(m.fields) && m.fields.length) return m.fields;
  if (typeof window === 'undefined') return [];
  const bs = window.builderState;
  if (bs?.mode === 'SUB') {
    return bs.sub?.currentConfig || m.item?.config || [];
  }
  const data = window.BUILDER_DATA;
  if (data?.shopifyBundle === true && Array.isArray(data.shopifyBundleMergedPersonalization)) {
    return data.shopifyBundleMergedPersonalization;
  }
  return data?.mainProduct?.config || data?.mainProduct?.personalization || [];
}

function flushDerivedPropertiesForManifest(manifest) {
  if (typeof window === 'undefined') return;
  const bs = window.builderState;
  if (!bs) return;
  const fieldConfig = resolveFieldConfigForFlush(manifest);
  if (!fieldConfig.length) return;
  const selections =
    bs.mode === 'SUB' ? bs.sub?.selections : bs.main?.selections;
  if (!selections) return;
  applyDerivedPropertiesToSelections(selections, fieldConfig);
}

/**
 * Main-flow AI image draft — DOM + builderState (not bundle row SUB scope).
 * PB-FND-65: finalize must emit Character URL for Lullaby-class lines.
 *
 * @param {string} [containerSelector]
 * @param {{ builderState?: object, collectFromDom?: Function }} [deps]
 */
export function resolveMainAiImageByLabel(containerSelector = '#main-flow-container', deps = {}) {
  const collectFromDom = deps.collectFromDom || collectAiImageDraftFromDom;
  const builderState =
    deps.builderState ?? (typeof window !== 'undefined' ? window.builderState : null);
  const domByLabel = collectFromDom(containerSelector) || {};
  const stateByLabel =
    builderState?.mode === 'SUB' && builderState.sub?.isQueueItem
      ? builderState.sub?.aiImageByLabel?.byLabel
      : builderState?.aiImageByLabel?.byLabel;

  const merged =
    stateByLabel && typeof stateByLabel === 'object'
      ? { ...stateByLabel, ...domByLabel }
      : { ...domByLabel };

  return Object.keys(merged).length ? { byLabel: merged } : undefined;
}

/**
 * Merge flushed DOM + session AI draft into builderState before encode.
 * SUB bundle rows persist on `sub.aiImageByLabel`; main flow on `aiImageByLabel`.
 * PB-FND-77: prevents stale autofill Character from winning over a re-generated row save.
 *
 * @param {string} [containerSelector]
 * @param {{ builderState?: object, collectFromDom?: Function }} [deps]
 */
export function persistMainAiImageDraftToState(containerSelector, deps = {}) {
  const builderState =
    deps.builderState ?? (typeof window !== 'undefined' ? window.builderState : null);
  if (!builderState) return;

  const merged = resolveMainAiImageByLabel(containerSelector, {
    builderState,
    collectFromDom: deps.collectFromDom,
  });
  if (!merged?.byLabel || !Object.keys(merged.byLabel).length) return;

  if (builderState.mode === 'SUB' && builderState.sub?.isQueueItem) {
    builderState.sub.aiImageByLabel = merged;
  } else {
    builderState.aiImageByLabel = merged;
  }
}

/**
 * Main-flow AI Text draft — DOM + builderState (not bundle row SUB scope).
 *
 * @param {string} [containerSelector]
 * @param {{ builderState?: object, collectFromDom?: Function }} [deps]
 */
export function resolveMainAiTextByLabel(containerSelector = '#main-flow-container', deps = {}) {
  const collectFromDom = deps.collectFromDom || collectAiTextDraftFromDom;
  const builderState =
    deps.builderState ?? (typeof window !== 'undefined' ? window.builderState : null);
  const domByLabel = collectFromDom(containerSelector) || {};
  const stateByLabel =
    builderState?.mode === 'SUB' && builderState.sub?.isQueueItem
      ? builderState.sub?.aiTextByLabel?.byLabel
      : builderState?.aiTextByLabel?.byLabel;

  const merged =
    stateByLabel && typeof stateByLabel === 'object'
      ? { ...stateByLabel, ...domByLabel }
      : { ...domByLabel };

  return Object.keys(merged).length ? { byLabel: merged } : undefined;
}

/**
 * Merge flushed DOM + session AI Text draft into builderState before encode.
 *
 * @param {string} [containerSelector]
 * @param {{ builderState?: object, collectFromDom?: Function }} [deps]
 */
export function persistMainAiTextDraftToState(containerSelector, deps = {}) {
  const builderState =
    deps.builderState ?? (typeof window !== 'undefined' ? window.builderState : null);
  if (!builderState) return;

  const merged = resolveMainAiTextByLabel(containerSelector, {
    builderState,
    collectFromDom: deps.collectFromDom,
  });
  if (!merged?.byLabel || !Object.keys(merged.byLabel).length) return;

  if (builderState.mode === 'SUB' && builderState.sub?.isQueueItem) {
    builderState.sub.aiTextByLabel = merged;
  } else {
    builderState.aiTextByLabel = merged;
  }
}

/**
 * @param {object} [stateSlice]
 * @param {object} [product]
 * @param {object} [pb]
 */
export function buildEncodeStateSlice(stateSlice, product, pb) {
  const slice = stateSlice && typeof stateSlice === 'object' ? stateSlice : {};
  const rawSelections = slice.selections || {};
  let catalogDesignLibrary = slice.catalogDesignLibrary;

  /* Same product ID as PDP main (e.g. Couple Set row 2) may borrow session
   * catalogDesignLibrary only from explicit row/main scope — never global leak
   * from another bundle row's SUB edit (PB-FND-64). */
  const mainProduct =
    typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct : null;
  const sameProductAsMain =
    !!(product && mainProduct && String(product.id) === String(mainProduct.id));
  const builderState = typeof window !== 'undefined' ? window.builderState : null;
  const subRowCatalog =
    builderState?.mode === 'SUB' &&
    builderState.sub?.isQueueItem &&
    builderState.sub?.catalogDesignLibrary
      ? builderState.sub.catalogDesignLibrary
      : null;

  if (!catalogDesignLibrary && product && !sameProductAsMain) {
    catalogDesignLibrary = null;
  } else if (!catalogDesignLibrary && subRowCatalog) {
    catalogDesignLibrary = subRowCatalog;
  } else if (!catalogDesignLibrary && typeof window !== 'undefined') {
    catalogDesignLibrary = builderState?.catalogDesignLibrary;
  }

  let selections = rawSelections;
  if (product && typeof pb?.flattenAiImageSelectionsForProps === 'function') {
    selections = pb.flattenAiImageSelectionsForProps(product, rawSelections);
  }
  if (product && typeof pb?.extractAiImagePropsFromSelections === 'function') {
    const aiFlat = pb.extractAiImagePropsFromSelections(product, rawSelections);
    selections = { ...selections };
    Object.keys(aiFlat || {}).forEach((key) => {
      if (aiFlat[key] != null && aiFlat[key] !== '') selections[key] = aiFlat[key];
    });
  }

  return {
    currentId: product?.id || slice.currentId,
    selections,
    topicSelections: slice.topicSelections || {},
    catalogDesignLibrary,
    aiImageByLabel: slice.aiImageByLabel,
    aiTextByLabel: slice.aiTextByLabel,
    variantId: slice.variantId,
  };
}

/**
 * Tier-1 encode — single entry for finalize, cart edit save, and add-to-cart.
 *
 * @param {object} [manifest]
 * @param {{ pb?: object, getPersonalizationFields?: Function }} [deps]
 */
export function encodeTier1Properties(manifest, deps) {
  const pb = deps?.pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  const m = manifest || {};
  const product =
    m.product ||
    m.fieldItem ||
    (typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct : null);
  const options = m.options || product?.options || [];
  const getFields =
    deps?.getPersonalizationFields ||
    (typeof pb?.getPersonalizationFields === 'function' ? pb.getPersonalizationFields.bind(pb) : null);
  const fields = m.fieldConfig || m.fields || (getFields ? getFields(product) : []);
  const encodeState = buildEncodeStateSlice(m.stateSlice || m.state || {}, product, pb);
  const extra = m.extraProps || {};

  assertEncodeBeltReady(pb, 'encodeTier1Properties');

  const props = pb.encodeBuilderStateForCart(encodeState, fields, options);
  Object.keys(extra).forEach((key) => {
    if (extra[key] != null && extra[key] !== '') props[key] = extra[key];
  });
  return props;
}

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBFlushEncode = {
    resolveFlowContainerSelector,
    resolveMainAiImageByLabel,
    persistMainAiImageDraftToState,
    resolveMainAiTextByLabel,
    persistMainAiTextDraftToState,
    flushBuilderState,
    buildEncodeStateSlice,
    encodeTier1Properties,
    areEncodeBeltReady,
    assertEncodeBeltReady,
    ENCODE_BELT_NOT_READY_MESSAGE,
  };
}

attachToWindow();
